import { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SEO from '../components/SEO';

export default function ContactPage() {
    const [loading, setLoading] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        // Simulate API call
        setTimeout(() => {
            toast.success('Message sent successfully! We will get back to you soon.');
            setLoading(false);
            e.target.reset();
        }, 1500);
    };

    return (
        <div className="min-h-screen bg-white">
            <SEO title="Contact Us" description="Get in touch with NSO India for any inquiries about our products and services." />
            {/* Hero Section */}
            <section className="bg-surface py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="max-w-3xl">
                        <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-4 block">
                            Contact Us
                        </span>
                        <h1 className="font-heading text-4xl md:text-6xl tracking-tight text-secondary mb-6">
                            How can we <span className="text-primary">help?</span>
                        </h1>
                        <p className="text-lg text-muted-foreground font-body leading-relaxed">
                            Have questions about our products, orders, or anything else? Our team is here to support you.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="grid lg:grid-cols-2 gap-16">
                        {/* Contact Form */}
                        <div className="animate-fade-in">
                            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-soft">
                                <h2 className="font-heading text-2xl text-secondary mb-8 flex items-center gap-3">
                                    <MessageSquare className="w-6 h-6 text-primary" />
                                    Send us a Message
                                </h2>
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-secondary">Full Name</label>
                                            <Input placeholder="John Doe" required className="rounded-xl h-12" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-secondary">Email Address</label>
                                            <Input type="email" placeholder="john@example.com" required className="rounded-xl h-12" />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-secondary">Subject</label>
                                        <Input placeholder="How can we help?" required className="rounded-xl h-12" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-secondary">Message</label>
                                        <Textarea
                                            placeholder="Tell us more about your inquiry..."
                                            required
                                            className="rounded-2xl min-h-[150px] py-4"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-primary hover:bg-primary/90 text-white rounded-full h-14 text-lg btn-press"
                                    >
                                        {loading ? 'Sending...' : (
                                            <>
                                                Send Message
                                                <Send className="w-5 h-5 ml-2" />
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-12">
                            <div>
                                <h2 className="font-heading text-2xl text-secondary mb-8">Contact Information</h2>
                                <div className="space-y-6">
                                    <div className="flex gap-4">
                                        <div className="w-12 h-12 bg-surface rounded-2xl flex items-center justify-center flex-shrink-0">
                                            <Phone className="w-6 h-6 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-body font-semibold text-secondary">Phone Number</p>
                                            <p className="text-muted-foreground">+91 7077314444</p>
                                            <p className="text-xs text-muted-foreground mt-1">Mon-Sat: 9 AM - 6 PM</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="w-12 h-12 bg-surface rounded-2xl flex items-center justify-center flex-shrink-0">
                                            <Mail className="w-6 h-6 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-body font-semibold text-secondary">Email Support</p>
                                            <p className="text-muted-foreground">support@nso-india.com</p>
                                            <p className="text-xs text-muted-foreground mt-1">Response time: Within 24 hours</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="w-12 h-12 bg-surface rounded-2xl flex items-center justify-center flex-shrink-0">
                                            <MapPin className="w-6 h-6 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-body font-semibold text-secondary">Our Office</p>
                                            <p className="text-muted-foreground">NSO Superstore, Sumitra Heights, Opp-New Court</p>
                                            <p className="text-muted-foreground">Kachery Road, Uditnagar, Rourkela-769012, Odisha, INDIA</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h2 className="font-heading text-2xl text-secondary mb-6 flex items-center gap-3">
                                    <Clock className="w-6 h-6 text-primary" />
                                    Business Hours
                                </h2>
                                <div className="bg-surface rounded-3xl p-6 space-y-3">
                                    <div className="flex justify-between border-b border-gray-100 pb-2">
                                        <span className="text-secondary">Monday - Friday</span>
                                        <span className="text-muted-foreground font-medium">9:00 AM - 7:00 PM</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-100 pb-2">
                                        <span className="text-secondary">Saturday</span>
                                        <span className="text-muted-foreground font-medium">10:00 AM - 4:00 PM</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-secondary">Sunday</span>
                                        <span className="text-destructive font-medium">Closed</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section className="h-[400px] w-full bg-surface grayscale opacity-80">
                <div className="h-full w-full flex items-center justify-center text-muted-foreground italic">
                    [ Google Map Integration Placeholder ]
                </div>
            </section>
        </div>
    );
}
